import React, { useEffect, useState, useCallback } from 'react';

const APPROVER_LABELS = ['HOD', 'FINANCE_MANAGER', 'GM', 'OWNER', 'ANY_ADMIN'];

const newRule = (rule_no) => ({
  rule_no,
  category_id: '',
  department_id: '',
  amount_from: '0',
  amount_to: '50000',
  approver_label: 'HOD',
  approver_user_id: ''
});

function PmsExpenseApprovalRule({ token, selectedProperty }) {
  const [rules, setRules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const headers = (extra = {}) => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
    ...extra
  });

  const loadData = useCallback(async () => {
    if (!selectedProperty?.id) return;
    try {
      const [rRes, cRes, dRes] = await Promise.all([
        fetch('/api/v1/expense/approval-rules', { headers: headers() }),
        fetch('/api/v1/expense/categories', { headers: headers() }),
        fetch('/api/v1/expense/departments', { headers: headers() })
      ]);
      const [rData, cData, dData] = await Promise.all([rRes.json(), cRes.json(), dRes.json()]);
      if (rRes.ok) setRules(rData.rows || []);
      if (cRes.ok) setCategories(cData.rows || []);
      if (dRes.ok) setDepartments(dData.rows || []);
      setError('');
    } catch { setError('Network error'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  useEffect(() => { loadData(); }, [loadData]);

  const updateRule = (idx, key, val) => {
    setRules(rs => rs.map((r, i) => i === idx ? { ...r, [key]: val } : r));
  };

  const addRule = () => {
    const nextNo = (rules.length === 0 ? 1 : Math.max(...rules.map(r => Number(r.rule_no) || 0)) + 1);
    setRules(rs => [...rs, newRule(nextNo)]);
  };

  const removeRule = (idx) => setRules(rs => rs.filter((_, i) => i !== idx));

  const saveAll = async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const payload = {
        rules: rules.map(r => ({
          ...r,
          category_id: r.category_id ? Number(r.category_id) : null,
          department_id: r.department_id ? Number(r.department_id) : null,
          amount_from: Number(r.amount_from || 0),
          amount_to: Number(r.amount_to || 0),
          approver_user_id: r.approver_user_id || null
        }))
      };
      const res = await fetch('/api/v1/expense/approval-rules', {
        method: 'PUT',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) return setError(data.error || 'Save failed');
      setMessage(`Saved ${data.count} rule(s).`);
      loadData();
    } catch { setError('Network error'); }
    finally { setSaving(false); }
  };

  const inputStyle = { padding: '0.4rem', border: '1px solid #d1d5db', borderRadius: 4, fontSize: '0.82rem' };

  return (
    <div className="page-shell">
      <h2 style={{ color: 'var(--theme-primary-dark)', marginBottom: '0.8rem' }}>Approval Rules</h2>
      {!selectedProperty?.id && <div className="error-banner">Select a property first.</div>}
      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.6rem 1rem', borderRadius: 6, marginBottom: '0.8rem' }}>{error}</div>}
      {message && <div style={{ background: '#dcfce7', color: '#166534', padding: '0.6rem 1rem', borderRadius: 6, marginBottom: '0.8rem' }}>{message}</div>}
      <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Rules are evaluated by rule_no ascending. The first match (by amount range, category, department) determines the required approver. Leave Category/Department blank to match all.</p>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '1rem' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: '#f3f4f6', textAlign: 'left' }}>
                <th style={{ padding: '0.5rem', width: 60 }}>#</th>
                <th style={{ padding: '0.5rem' }}>Category</th>
                <th style={{ padding: '0.5rem' }}>Department</th>
                <th style={{ padding: '0.5rem' }}>Amount From</th>
                <th style={{ padding: '0.5rem' }}>Amount To</th>
                <th style={{ padding: '0.5rem' }}>Approver</th>
                <th style={{ padding: '0.5rem', width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {rules.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: '1.5rem', textAlign: 'center', color: '#6b7280' }}>No approval rules. Click "Add Rule" to start.</td></tr>
              ) : rules.map((r, idx) => (
                <tr key={idx} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '0.4rem' }}><input type="number" value={r.rule_no} onChange={e => updateRule(idx, 'rule_no', Number(e.target.value))} style={{ ...inputStyle, width: 50 }} /></td>
                  <td style={{ padding: '0.4rem' }}>
                    <select value={r.category_id || ''} onChange={e => updateRule(idx, 'category_id', e.target.value)} style={inputStyle}>
                      <option value="">All</option>
                      {categories.map(c => <option key={c.category_id} value={c.category_id}>{c.category_name}</option>)}
                    </select>
                  </td>
                  <td style={{ padding: '0.4rem' }}>
                    <select value={r.department_id || ''} onChange={e => updateRule(idx, 'department_id', e.target.value)} style={inputStyle}>
                      <option value="">All</option>
                      {departments.map(d => <option key={d.department_id} value={d.department_id}>{d.dept_name}</option>)}
                    </select>
                  </td>
                  <td style={{ padding: '0.4rem' }}><input type="number" step="0.01" value={r.amount_from} onChange={e => updateRule(idx, 'amount_from', e.target.value)} style={{ ...inputStyle, width: 100 }} /></td>
                  <td style={{ padding: '0.4rem' }}><input type="number" step="0.01" value={r.amount_to} onChange={e => updateRule(idx, 'amount_to', e.target.value)} style={{ ...inputStyle, width: 100 }} /></td>
                  <td style={{ padding: '0.4rem' }}>
                    <select value={r.approver_label} onChange={e => updateRule(idx, 'approver_label', e.target.value)} style={inputStyle}>
                      {APPROVER_LABELS.map(l => <option key={l} value={l}>{l}</option>)}
                    </select>
                  </td>
                  <td style={{ padding: '0.4rem' }}><button onClick={() => removeRule(idx)} style={{ padding: '0.25rem 0.5rem', border: '1px solid #ef4444', borderRadius: 4, background: '#fff', color: '#ef4444', cursor: 'pointer', fontSize: '0.78rem' }}>×</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '1rem' }}>
          <button onClick={addRule} style={{ padding: '0.5rem 1rem', border: '1px dashed #3b82f6', borderRadius: 6, background: '#fff', color: '#3b82f6', cursor: 'pointer' }}>+ Add Rule</button>
          <button onClick={saveAll} disabled={saving} style={{ padding: '0.5rem 1.2rem', border: 'none', borderRadius: 6, background: '#2563eb', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>{saving ? 'Saving…' : 'Save All Rules'}</button>
        </div>
      </div>
    </div>
  );
}

export default PmsExpenseApprovalRule;
